import isEmpty from 'lodash/isEmpty';

import { TFunction, Trans } from '@lyearn/i18n';
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
import AIPraiseMessage from '@/components/LyearnAI/components/Editor/components/AIPraiseMessage';
import { UserFragment } from '@/graphql/fragments/generated/User';
import { TextUseCaseEnum } from '@/types/schema';

import { PraiseDetailsFragment } from '../../../../graphql/fragments/generated/PraiseDetails';
import PraiseRecipients from '../PraiseRecipients';
import PraiseRelatedMindsets from '../PraiseRelatedMindsets';
import PraiseRelatedSkills from '../PraiseRelatedSkills';
import PraiseVisibility from '../PraiseVisibility';

const EMPTY_ARRAY: never[] = [];

export const getInitialPraiseValues = ({
  praise,
  users,
}: {
  praise?: PraiseDetailsFragment;
  users?: UserFragment[];
}) => {
  return {
    receiverUsers: praise?.receiverUsers || users || EMPTY_ARRAY,
    message: praise?.post?.message || '',
    medias: praise?.post?.attachments?.map((attachment) => attachment.media),
    mentions:
      praise?.post?.mentions?.map(({ entityId, entityType }) => ({ entityId, entityType })) ||
      EMPTY_ARRAY,
    visibleToEveryone: praise?.visibleToEveryone !== undefined ? praise.visibleToEveryone : true,
    skillRatings: praise?.skillRatings?.map((skill) => ({ skillId: skill.skillId })) || EMPTY_ARRAY,
    mindsetRatings:
      praise?.mindsetRatings?.map((mindset) => ({ mindsetId: mindset.mindsetId })) || EMPTY_ARRAY,
  };
};

export type GivePraiseFormValues = ReturnType<typeof getInitialPraiseValues>;

export const getPraiseFormFields = ({ t }: { t: TFunction }): Fields<GivePraiseFormValues> => {
  return {
    receiverUsers: {
      name: 'receiverUsers',
      label: t('Praises:Form.PraiseFor'),
      type: FieldType.Custom,
      Component: ({ value, onChange, error, touched, setFieldValue }) => {
        return (
          <PraiseRecipients
            error={Boolean(touched && error)}
            setFieldValue={setFieldValue}
            value={value}
            onChange={onChange}
          />
        );
      },
    },
    message: {
      name: 'message',
      label: t('Praises:Form.Message'),
      type: FieldType.SlowCustom,
      Component: ({ value, touched, error, onChange, setFieldValue, formik }) => {
        const medias = useFormFieldValue('medias');
        return (
          <AIPraiseMessage
            error={Boolean(touched && error)}
            medias={medias || EMPTY_ARRAY}
            placeholder={t('Praises:DescriptionPlaceholder')}
            setFieldValue={setFieldValue}
            textUseCase={TextUseCaseEnum.UserPraise}
            topic={value}
            value={value}
            onChange={onChange}
            onTopicError={() => {
              formik.setFieldTouched('message', true);
              formik.setFieldError('message', 'Write a word or two for AI to work its magic!');
            }}
          />
        );
      },
    },
    visibleToEveryone: {
      name: 'visibleToEveryone',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => <PraiseVisibility value={value} onChange={onChange} />,
    },
    skillRatings: {
      name: 'skillRatings',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        const receiverUsers = useFormFieldValue('receiverUsers');

        return (
          <PraiseRelatedSkills receiverUsers={receiverUsers} value={value} onChange={onChange} />
        );
      },
    },
    mindsetRatings: {
      name: 'mindsetRatings',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        const receiverUsers = useFormFieldValue('receiverUsers');

        return (
          <PraiseRelatedMindsets receiverUsers={receiverUsers} value={value} onChange={onChange} />
        );
      },
    },
  };
};

export const getPraiseSectionLayout = (t: TFunction) => [
  {
    id: 'basicDetails',
    layout: ['receiverUsers', 'message', 'visibleToEveryone'],
  },
  {
    id: 'skills',
    name: t('Praises:Form.RelatedSkills'),
    layout: ['skillRatings'],
  },
  {
    id: 'mindset',
    name: t('Praises:Form.RelatedMindsets'),
    layout: ['mindsetRatings'],
  },
];

export const validatePraiseForm = (values: GivePraiseFormValues) => {
  const errors: any = {};

  if (isEmpty(values.receiverUsers)) {
    errors.receiverUsers = <Trans i18nKey="Praises:Form.PraiseReceiverEmptyError" />;
  }

  if (isEmpty(values.message)) {
    errors.message = <Trans i18nKey="Praises:Form.PraiseMessageEmptyError" />;
  }

  return errors;
};
